import React from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import SalePaymentGetByTokenQuery from './SalePaymentGetByTokenQuery';
import { SalePaymentResponseData } from './types';
import SalePaymentMethodsQuery from './SalePaymentMethodsQuery';
import { PaymentMethod } from './types';
import { useNavigate } from 'react-router-dom';

type result = {
  loading: boolean;
  payment?: SalePaymentResponseData;
  paymentMethods?: PaymentMethod[];
};

interface ISavePaymentProductsConnectorApollo {
  children: (result: result) => React.ReactElement;
  token: string;
  getPaymentMethods?: boolean;
  clonePayment?: () => Promise<unknown>;
  pathPaymentMethods?: string;
}

const SalePaymentGetByTokenConnectorApollo: React.FC<ISavePaymentProductsConnectorApollo> = ({
  children,
  token,
  getPaymentMethods = true,
  clonePayment,
  pathPaymentMethods,
}) => {
  const navigate = useNavigate();

  const checkPaymentStatus = async (payment: any) => {
    if (payment.status !== 'init' && !payment.isCod) {
      if (clonePayment && pathPaymentMethods) {
        const clonedPayment: any = await clonePayment();
        const urlWithNewToken = pathPaymentMethods.replace(
          ':token',
          clonedPayment.data.sale.clonePayment.token
        );
        navigate(urlWithNewToken);
      }
    }

    // if the user goes back from thankyou page to payment methods page if the payment is COD
    if (payment.status === 'pending' && payment.isCod) {
      const landingPageUrl = import.meta.env.VITE_LANDING_PAGE_URL;
      if (typeof landingPageUrl === 'string') {
        window.location.href = landingPageUrl;
      }
    }
  };

  const [
    fetchPaymentMethods,
    { loading: loadingFetchPaymentMethods, data: paymentMethods, error: errorPaymentMethods },
  ] = useLazyQuery(SalePaymentMethodsQuery);

  const {
    loading: loadingSalePayment,
    error: errorSalePayment,
    data,
  } = useQuery(SalePaymentGetByTokenQuery, {
    variables: {
      token,
    },

    onCompleted: ({ sale }) => {
      const payment = sale.payment.getByToken;
      if (clonePayment && pathPaymentMethods) {
        checkPaymentStatus(payment);
      }

      if (getPaymentMethods) {
        const amount = sale.payment.getByToken.products[0].price.amount;
        fetchPaymentMethods({ variables: { amount } });
      }
    },
  });

  const loading = loadingSalePayment || loadingFetchPaymentMethods;

  const result: result = {
    loading,
  };

  if (errorSalePayment) {
    throw errorSalePayment;
  }

  if (errorPaymentMethods) {
    throw errorSalePayment;
  }

  if (!loading) {
    if (!data.sale.payment.getByToken) {
      global.location.href = String(import.meta.env.VITE_LANDING_PAGE_URL);
    }

    result.payment = data.sale.payment.getByToken;
    if (getPaymentMethods && paymentMethods) {
      result.paymentMethods = paymentMethods.sale.payment.methods;
    }
  }

  return children(result);
};

export default SalePaymentGetByTokenConnectorApollo;
